import { BUILDINGS, BuildingType, type BuildingView, type Command, JUNCTION_TOWER } from '@am/sim';

/*
 * The free Android game's rules (see full-version.ts), apart so they can be tested without the
 * game around them: where the free game stops, and which commands go past it.
 */

/** The one-time product in Play Console (Monetize → In-app products). */
export const FULL_VERSION_PRODUCT = 'full_version';
/** The highest tower level the free game reaches. */
export const FREE_TOWER_LEVEL = 2;
/** Why the full version was offered: what the player tried to do. */
export type PaywallReason = 'building' | 'upgrade' | 'tower' | 'junction' | 'map' | 'coop';

/** Whether this game is the free one with a full version to buy: the Android app. */
export function sellsFullVersion(): boolean {
  if (import.meta.env.MODE !== 'app') return false;
  const cap = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  return cap?.getPlatform?.() === 'android';
}

/**
 * What a command needs that the free game lacks (null: nothing): a building, an upgrade or a
 * junction kind the tower must be past FREE_TOWER_LEVEL for, the tower itself past it.
 */
export function commandNeedsFull(
  command: Command,
  buildings: readonly BuildingView[],
): PaywallReason | null {
  if (command.type === 'placeBuilding') {
    const def = BUILDINGS[command.building as keyof typeof BUILDINGS];
    return (def?.levels[0]?.requiresTower ?? 0) > FREE_TOWER_LEVEL ? 'building' : null;
  }
  if (command.type === 'upgradeBuilding') {
    const b = buildings.find((x) => x.id === command.id);
    if (!b) return null;
    if (b.type === BuildingType.Tower && b.level >= FREE_TOWER_LEVEL) return 'tower';
    const next = BUILDINGS[b.type].levels[b.level];
    return (next?.requiresTower ?? 0) > FREE_TOWER_LEVEL ? 'upgrade' : null;
  }
  if (command.type === 'setJunction') {
    return (JUNCTION_TOWER[command.kind] ?? 0) > FREE_TOWER_LEVEL ? 'junction' : null;
  }
  return null;
}
